// @flow

import React, { PureComponent } from "react";
import { noop, get, map } from "lodash";
import { formatDateWithText } from "@tvg/formatter/dates";
import { formatSequencial } from "@tvg/formatter";
import { formatPurse } from "@tvg/formatter/currency";
import type { RaceProgram, RaceListItem } from "@tvg/types/Race";
import type { PastPerformanceRace as PastPerformanceRaceType } from "@fdr/types/PastPerformance";
import type { NullaryFn } from "@tvg/types/Functional";
import Icon from "@fdr/static-ui/Icons";
import { arrowLeft, arrowRight, videoReplay } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import Tag from "../../_atoms/Tag";

import {
  RaceButton,
  Date,
  RaceInfo,
  TrackRace,
  RaceDataList,
  RaceDataListItem,
  RaceSection,
  RaceArrowLink,
  RaceArrowNavigation,
  RaceArrowContent,
  ResultItem,
  Position,
  ReplayButton,
  RaceNavigation,
  TrackInfo,
  DateWrapper
} from "./styled-components";
import MTP from "../../_atoms/MTP";
import { formatOdd } from "../RunnerLine";
import RaceInfoInline from "../../_atoms/RaceInfoInline";

type Props = {
  race: ?PastPerformanceRaceType,
  onClick: NullaryFn<void>,
  hasNextRace: boolean,
  hasPreviousRace: boolean,
  useSimpleLayout: boolean,
  onPreviousRaceNavigation: NullaryFn<void>,
  onNextRaceNavigation: NullaryFn<void>,
  currentRace: ?RaceProgram
};

export default class PastPerformanceRace extends PureComponent<Props> {
  static defaultProps = {
    onClick: noop,
    hasNextRace: false,
    hasPreviousRace: false,
    useSimpleLayout: false,
    onPreviousRaceNavigation: noop,
    onNextRaceNavigation: noop,
    currentRace: null
  };

  buildRaceData = (race: ?PastPerformanceRaceType): RaceListItem[] => {
    const raceCodeMap = { Q: "Quarterhorse", T: "Thoroughbred", H: "Harness" };
    return [
      "numRunners",
      "raceTypeCode",
      "distance",
      "surface.shortName",
      "surface.defaultCondition"
    ].map((field) => {
      if (field === "raceTypeCode") {
        return {
          value: get(raceCodeMap, get(race, field)),
          changed: false
        };
      }
      if (field === "numRunners") {
        return {
          value: `${get(race, field)} Runners`,
          changed: false
        };
      }
      return { value: get(race, field), changed: false };
    });
  };

  renderDate = () => {
    if (!this.props.currentRace) {
      const date = formatDateWithText(get(this.props, "race.date"));

      return <Date data-qa-label="race-info-date">{date}</Date>;
    }

    return (
      <MTP
        isNewLayout
        isSmall
        showDescription={false}
        size={12}
        mode="light"
        mtp={get(this.props, "currentRace.mtp")}
        postTime={get(this.props, "currentRace.postTime")}
        status={get(this.props, "currentRace.status.code")}
      />
    );
  };

  renderSimpleInfo = (race: ?PastPerformanceRaceType) => (
    <RaceNavigation>
      <RaceInfo>
        <TrackInfo>
          {`${get(race, "track.name")} R${get(race, "raceNumber", 0)}`}
        </TrackInfo>
        <RaceInfoInline
          textColor={buildColor("grey", "800")}
          raceData={this.buildRaceData(race)}
        />
      </RaceInfo>
      {this.renderNavigation()}
    </RaceNavigation>
  );

  renderInfo = (
    race: ?RaceProgram | PastPerformanceRaceType,
    isPastRace: boolean
  ) => {
    const raceDataList = {
      distance: get(race, "distance"),
      surface: get(race, "surface.shortName"),
      defaultCondition: get(race, "surface.defaultCondition"),
      purse: formatPurse(get(race, "purse"))
    };

    return (
      <RaceInfo>
        <TrackRace
          isCurrentRace={!isPastRace}
          data-qa-label="race-track-number"
        >
          {`${
            isPastRace ? get(race, "track.name") : get(race, "track.trackName")
          }`}{" "}
          {isPastRace && this.renderDate()}
        </TrackRace>
        <RaceDataList>
          {map(
            raceDataList,
            (data, key) =>
              data && (
                <RaceDataListItem
                  key={`race-${key}`}
                  data-qa-label={`race-info-${key}`}
                >
                  {data}
                </RaceDataListItem>
              )
          )}
        </RaceDataList>
        {!isPastRace && <DateWrapper>{this.renderDate()}</DateWrapper>}
        {isPastRace && (
          <RaceDataList hasMarginTop>
            <ResultItem>
              Odds{" "}
              <span>
                {get(race, "currentOdds.numerator") !== undefined
                  ? formatOdd(
                      get(race, "currentOdds.numerator"),
                      get(race, "currentOdds.denominator")
                    )
                  : "N/A"}
              </span>
            </ResultItem>
            <ResultItem>
              Position <span>{this.renderPosition()}</span>
            </ResultItem>
          </RaceDataList>
        )}
      </RaceInfo>
    );
  };

  renderPosition = () => {
    if (this.props.race) {
      const { finishPosition, numRunners } = this.props.race;
      switch (finishPosition) {
        case null:
          return <Tag tagType="CoupledTag" name="N/A" />;
        case 99:
          return <Tag tagType="CoupledTag" name="DNF" />;
        default: {
          const isPodium = +finishPosition <= 3;
          return (
            <Position isPodium={isPodium}>{`${formatSequencial(
              +finishPosition
            )}/${numRunners}`}</Position>
          );
        }
      }
    }
    return null;
  };

  renderReplay = (isPastRace: boolean) => {
    const hasReplay = get(this.props, "race.hasReplays");

    return (
      isPastRace && (
        <ReplayButton isCurrentRace={!isPastRace}>
          <Icon
            data-qa-label={hasReplay ? "video-replay" : "no-video-replay"}
            size={16}
            color={
              hasReplay ? buildColor("blue", "D1") : buildColor("grey", "400")
            }
            icon={videoReplay}
          />
        </ReplayButton>
      )
    );
  };

  renderNavigation = () => {
    if (!this.props.hasNextRace && !this.props.hasPreviousRace) {
      return null;
    }

    const renderButton = (icon, callback, type: "back" | "forward") => {
      const buttonContent = (
        <RaceArrowContent
          {...(!callback
            ? { "data-qa-label": `ipp-navigation-${type}-disable` }
            : {})}
        >
          <Icon
            icon={icon}
            size={17}
            color={
              callback ? buildColor("blue", "D1") : buildColor("grey", "300")
            }
          />
        </RaceArrowContent>
      );

      return callback ? (
        <RaceArrowLink onClick={callback} qaLabel={`ipp-navigation-${type}`}>
          {buttonContent}
        </RaceArrowLink>
      ) : (
        buttonContent
      );
    };

    return (
      <RaceArrowNavigation>
        {renderButton(
          arrowLeft,
          this.props.hasPreviousRace
            ? this.props.onPreviousRaceNavigation
            : null,
          "back"
        )}
        {renderButton(
          arrowRight,
          this.props.hasNextRace ? this.props.onNextRaceNavigation : null,
          "forward"
        )}
      </RaceArrowNavigation>
    );
  };

  renderSimpleLayout = () => (
    <RaceSection data-qa-label="ipp-past-race">
      {this.renderSimpleInfo(this.props.race)}
    </RaceSection>
  );

  renderFullLayout = (isPastRace: boolean) => (
    <RaceButton onClick={this.props.onClick}>
      {this.renderInfo(
        isPastRace ? this.props.race : this.props.currentRace,
        isPastRace
      )}
      {this.renderReplay(isPastRace)}
    </RaceButton>
  );

  render() {
    const isPastRace = !this.props.currentRace;

    if (this.props.useSimpleLayout) {
      return this.renderSimpleLayout();
    }

    return this.renderFullLayout(isPastRace);
  }
}
